import $mediator from '@whitetail/buckshot/scripts/buckshot-mediator';
import api from './api';

function resetUserData() {
    return {
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        avatar: null,
        hasPassword: null,
        zipcode: null,
        facebookConnected: null,
        googleConnected: null,
        listingCount: null,
        searchCount: null,
        mediaCount: null,
        notifications: {
            newsletter: false,
            listings: 'never',
        },
    };
}

function toCamel(value) {
    return value.replace(/_(.)/g, (camelStr) => camelStr.toUpperCase())
        .replace(/_/g, '')
        .replace(/^(.)/, (camelStr) => camelStr.toLowerCase());
}

class MyWP {
    constructor() {
        this.isLoggedIn = false;
        this.savedListingIds = [];
        this.savedMediaIds = [];
        this.savedSearches = [];
        this.userData = resetUserData();
        this.statusSet = false;
        this.checkStatus();
    }

    checkLogin() {
        if (! this.isLoggedIn) {
            throw new Error('unauthenticated');
        }
    }

    async doLogin($username, $password, $remember = false) {
        await api.post('/mywp/login', {
            $username,
            $password,
            $remember,
        })
            .then((response) => {
                this.isLoggedIn = true;
                this.loadUserData(response.data.data);
            })
            .catch(() => {
                this.userData = resetUserData();
                this.isLoggedIn = false;
            });

        return this.isLoggedIn;
    }

    async doLogout() {
        await api.get('/mywp/user/logout');

        this.userData = resetUserData();
        this.isLoggedIn = false;
    }

    checkStatus() {
        this.getStatus();

        return this.isLoggedIn;
    }

    async getStatus() {
        await api.get('/mywp/login/status')
            .then((response) => {
                this.isLoggedIn = response.data.authenticated;

                this.userData.firstName = response.data.first_name;
                this.userData.lastName = response.data.last_name;
                this.userData.avatar = response.data.avatar;
                this.userData.email = response.data.email;
                this.userData.id = response.data.user_id;

                $mediator.emit('statusUpdated');
                this.statusSet = true;
            })
            .catch((error) => {
                console.log(error); // eslint-disable-line no-console
            });
    }

    async getUser() {
        if (! this.userData.id) {
            const response = await api.get('/mywp/user');

            this.loadUserData(response.data.data);
        }

        return this.userData;
    }

    loadUserData(payload) {
        this.userData = {
            userId: payload.id,
            hasPassword: payload.password_set,
            avatar: payload.avatar,
            email: payload.email,
            firstName: payload.first_name,
            lastName: payload.last_name,
            zipcode: payload.zipcode,
            facebookConnected: payload.facebook_connect !== null,
            googleConnected: payload.google_connect !== null,
            listingCount: payload.listings_count,
            searchCount: payload.search_count,
            mediaCount: payload.media_count,
            notifications: payload.notifications,
            gerr: payload.gerr ? payload.gerr : false,
            ferr: payload.ferr ? payload.ferr : false,
            gemail: payload.gemail ? payload.gemail : false,
            femail: payload.femail ? payload.femail : false,
            gusr: payload.gusr ? payload.gusr : false,
            fusr: payload.fusr ? payload.fusr : false,
        };
    }

    loadUserParam(param, value) {
        if (typeof value === 'object') {
            Object.keys(value).forEach((key) => {
                const camelKey = toCamel(key);

                this.userData[camelKey] = value[key];
            });
        } else {
            this.userData[param] = value;
        }
    }

    async setAvatar(avatarForm) {
        this.checkLogin();

        const response = await api.post('/mywp/user/avatar', avatarForm, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        this.userData.avatar = response.data.avatar;

        return this.userData.avatar;
    }

    async setUserParam(setting, value) {
        const response = await api.post(`/mywp/user/${setting}`, value);

        this.loadUserParam(setting, value);

        return response.data;
    }

    async setEmail(email, password) {
        let success = true;

        await api.post('/mywp/user/email', {
            email,
            password,
        })
            .then((response) => {
                this.loadUserParam('email', email);

                success = response.data.update;
            })
            .catch((error) => {
                if (error.response.status === 401) {
                    throw new Error('unauthorized');
                } else if (error.response.status === 422) {
                    if (error.response.data.errors.password?.[0] === 'This field is required.') {
                        throw new Error('required');
                    } else if (error.response.data.errors.email?.[0] === 'The email must be a valid email address.') {
                        throw new Error('invalid');
                    } else {
                        throw new Error('taken');
                    }
                }
            });

        return success;
    }

    async disconnectProvider(providerName) {
        const provider = providerName.toLowerCase();
        const providerFirst = providerName[0];

        await api.delete('/mywp/detach/' + provider, {
            params: {
                _method: 'DELETE',
            },
        })
            .then((response) => {
                if (response.data.success) {
                    this.loadUserParam(provider + 'Connected', false);
                } else {
                    this.loadUserParam(providerFirst + 'err', true);
                }
            })
            .catch(() => {
                this.loadUserParam(providerFirst + 'err', true);
            });
    }

    async setPassword(curpass, password, passwordConfirmation) {
        let success = false;

        await api.post('/mywp/user/password', {
            curpass,
            password,
            password_confirmation: passwordConfirmation,
        })
            .then((response) => {
                success = response.data.success;

                this.loadUserParam('hasPassword', true);
            })
            .catch((error) => {
                if (error.response.status === 401) {
                    throw new Error('unauthorized');
                } else if (error.response.status === 422) {
                    if (error.response.data.errors.curpass?.length) {
                        throw new Error('curpass');
                    } else if (error.response.data.errors.password?.length) {
                        throw new Error('complexity');
                    }
                }
            });

        return success;
    }

    async unsubscribeAll() {
        return api.delete('/mywp/notifications/unsubscribe')
            .then((response) => {
                this.userData.notifications = response.data.notifications;

                return this.userData.notifications;
            });
    }

    async unsubscribeSearch() {
        return api.delete('/mywp/notifications/searches')
            .then((response) => {
                this.savedSearches = response.data.data;
                this.userData.searchCount = this.savedSearches.length;

                return this.savedSearches;
            });
    }

    async updateNotifications(payload) {
        return api.post('/mywp/notifications', payload)
            .then((response) => {
                this.savedSearches = response.data.searches;
                this.userData.searchCount = this.savedSearches.length;
                this.userData.notifications = response.data.notifications;

                return this.userData.notifications;
            });
    }
}

const mywp = new MyWP();

export default mywp;
